import React from 'react';
import { Link } from 'react-router-dom';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { ImageIcon, MailIcon, TagIcon, LayoutIcon, TrendingUpIcon, UsersIcon, DollarSignIcon, PercentIcon, PlusIcon, CalendarIcon, CheckCircleIcon, ClockIcon, ArrowRightIcon, MegaphoneIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
const MarketingTools = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  // Mock Stats
  const stats = [{
    label: 'Total Promo Revenue',
    value: '$4,550',
    change: '+12%',
    icon: DollarSignIcon,
    color: 'text-green-500',
    bg: 'bg-green-100',
    darkBg: 'bg-green-900/20'
  }, {
    label: 'Email Subscribers',
    value: '2,450',
    change: '+8%',
    icon: UsersIcon,
    color: 'text-blue-500',
    bg: 'bg-blue-100',
    darkBg: 'bg-blue-900/20'
  }, {
    label: 'Avg. Open Rate',
    value: '42%',
    change: '+2%',
    icon: MailIcon,
    color: 'text-purple-500',
    bg: 'bg-purple-100',
    darkBg: 'bg-purple-900/20'
  }, {
    label: 'Active Campaigns',
    value: '3',
    change: '0',
    icon: MegaphoneIcon,
    color: 'text-orange-500',
    bg: 'bg-orange-100',
    darkBg: 'bg-orange-900/20'
  }];
  // Quick Actions
  const quickActions = [{
    title: 'Create Promotion',
    description: 'Run a sale or deal',
    icon: PercentIcon,
    to: '/vendors/manage?type=sale',
    color: 'bg-green-500'
  }, {
    title: 'New Email Campaign',
    description: 'Send a newsletter',
    icon: MailIcon,
    to: '/vendors/marketing/email-campaigns',
    color: 'bg-blue-500'
  }, {
    title: 'Create Graphic',
    description: 'Design social assets',
    icon: ImageIcon,
    to: '/vendors/marketing/promo-graphics',
    color: 'bg-purple-500'
  }, {
    title: 'Browse Templates',
    description: 'Social media kits',
    icon: LayoutIcon,
    to: '/vendors/marketing/social-templates',
    color: 'bg-orange-500'
  }];
  // Recent Activity
  const recentActivity = [{
    id: 1,
    action: 'Sent Newsletter',
    target: 'October Updates',
    time: '2 hours ago',
    status: 'Sent',
    icon: MailIcon,
    iconColor: 'text-blue-500',
    iconBg: 'bg-blue-100'
  }, {
    id: 2,
    action: 'Created Discount',
    target: 'SPOOKY20',
    time: '1 day ago',
    status: 'Active',
    icon: TagIcon,
    iconColor: 'text-green-500',
    iconBg: 'bg-green-100'
  }, {
    id: 3,
    action: 'Downloaded Template',
    target: 'Halloween Social Kit',
    time: '3 days ago',
    status: 'Completed',
    icon: LayoutIcon,
    iconColor: 'text-purple-500',
    iconBg: 'bg-purple-100'
  }];
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-19931" data-mp="16tzpehu7wwpf">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-19932" data-mp="12sejxhk0jkp9">
        <div className="mb-6" data-id="element-19933" data-mp="12ps2mq7blqtu">
          <BackButton data-id="element-19934" data-mp="1gizvsmb1uudn" />
        </div>

        <div className="flex flex-col md:flex-row md:items-center justify-between mb-8" data-id="element-19935" data-mp="1gk1dh9mkmf4c">
          <div data-id="element-19936" data-mp="1bri0oevpjo0l">
            <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-19937" data-mp="1br00x89u6rnx">
              Marketing Hub
            </h1>
            <p className={`mt-2 ${dk.textSecondary}`} data-id="element-19938" data-mp="1qjjy7sab24xg">
              Manage your campaigns, promotions, and creative assets.
            </p>
          </div>
          <div className="mt-4 md:mt-0" data-id="element-19939" data-mp="1zidnsyp7xv1i">
            <Link to="/vendors/manage?type=sale" className={`inline-flex items-center px-4 py-2 rounded-lg text-white font-medium transition-colors ${rt.bgDark} hover:opacity-90`} data-id="element-19940" data-mp="1zg1bb128ccvu">
              <PlusIcon className="h-5 w-5 mr-2" data-id="element-19941" data-mp="51axckosgnj" />
              Create New Campaign
            </Link>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-10" data-id="element-19942" data-mp="2j50lb25fiqh">
          {stats.map(stat => <div key={stat.label} className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-19943" data-mp="6ncs1fzzrcse">
              <div className="flex justify-between items-start mb-4" data-id="element-19944" data-mp="gjzdo70567vh">
                <div className={`p-3 rounded-lg ${darkMode ? stat.darkBg : stat.bg}`} data-id="element-19945" data-mp="gh3d0awwt53c">
                  <stat.icon className={`h-6 w-6 ${stat.color}`} data-id="element-19946" data-mp="3jzwlwaeqt5r6" />
                </div>
                <span className={`inline-flex items-center text-xs font-medium px-2 py-1 rounded-full ${stat.change.startsWith('+') ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-19947" data-mp="3oz9bv40wusxz">
                  {stat.change.startsWith('+') && <TrendingUpIcon className="h-3 w-3 mr-1" data-id="element-19948" data-mp="317u58z23ppgx" />}
                  {stat.change}
                </span>
              </div>
              <h3 className={`text-sm font-medium ${dk.textMuted}`} data-id="element-19949" data-mp="2oqj9aawyn9bn">
                {stat.label}
              </h3>
              <p className={`text-2xl font-bold mt-1 ${dk.textHeading}`} data-id="element-19950" data-mp="2jqyemcotpn4q">
                {stat.value}
              </p>
            </div>)}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-19951" data-mp="2ar1ixr40no06">
          {/* Quick Actions */}
          <div className="lg:col-span-2 space-y-8" data-id="element-19952" data-mp="260uwnxwz8fl3">
            <section data-id="element-19953" data-mp="25z0bqjlpgnh0">
              <h2 className={`text-xl font-bold mb-4 ${dk.textHeading}`} data-id="element-19954" data-mp="25yi831wpr6z2">
                Quick Actions
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-19955" data-mp="1w2nh9jic65l7">
                {quickActions.map(action => <Link key={action.title} to={action.to} className={`group p-4 rounded-xl border transition-all hover:shadow-md flex items-center ${dk.card} ${darkMode ? 'hover:border-gray-600' : 'hover:border-purple-200'}`} data-id="element-19956" data-mp="1r3kd01a6k3ea">
                    <div className={`h-12 w-12 rounded-lg flex items-center justify-center mr-4 text-white shadow-sm ${action.color}`} data-id="element-19957" data-mp="1hgrhvzsjfjnz">
                      <action.icon className="h-6 w-6" data-id="element-19958" data-mp="18ei7rb16lsb4" />
                    </div>
                    <div data-id="element-19959" data-mp="1w4kvxg1z6e4g">
                      <h3 className={`font-bold group-hover:${rt.textPrimary} transition-colors ${dk.textPrimary}`} data-id="element-19960" data-mp="1w4cm5x15foxh">
                        {action.title}
                      </h3>
                      <p className={`text-sm ${dk.textMuted}`} data-id="element-19961" data-mp="1me9t5wu12az5">
                        {action.description}
                      </p>
                    </div>
                    <ArrowRightIcon className={`ml-auto h-5 w-5 opacity-0 group-hover:opacity-100 transition-opacity transform group-hover:translate-x-1 ${dk.textFaint}`} data-id="element-19962" data-mp="2f2x7t9k9u23q" />
                  </Link>)}
              </div>
            </section>

            {/* Tools Links */}
            <section data-id="element-19963" data-mp="2p6m3do9lt7wk">
              <div className="flex items-center justify-between mb-4" data-id="element-19964" data-mp="2p5azzpk2m25z">
                <h2 className={`text-xl font-bold ${dk.textHeading}`} data-id="element-19965" data-mp="32xh4ufhrwmq4">
                  Your Tools
                </h2>
              </div>
              <div className={`rounded-xl border overflow-hidden ${dk.card}`} data-id="element-19966" data-mp="2y21sxt3bt0wc">
                <div className={`divide-y ${dk.divider}`} data-id="element-19967" data-mp="2aaej6ny746ha">
                  <Link to="/vendors/marketing/promotions" className={`p-4 flex items-center justify-between hover:${dk.surfaceMuted} transition-colors`} data-id="element-19968" data-mp="269b6gabg2qka">
                    <div className="flex items-center" data-id="element-19969" data-mp="2f8g2n9zja7ta">
                      <div className={`p-2 rounded-md mr-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-19970" data-mp="3vn7k4rv55unt">
                        <PercentIcon className={`h-5 w-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-19971" data-mp="3qnuvq2omgfj4" />
                      </div>
                      <div data-id="element-19972" data-mp="1t1lq5mu5k9wb">
                        <h3 className={`font-medium ${dk.textPrimary}`} data-id="element-19973" data-mp="1sypslzm7vn8e">
                          Promotions & Discounts
                        </h3>
                        <p className={`text-sm ${dk.textMuted}`} data-id="element-19974" data-mp="22qvhh0h69ojd">
                          Manage sales, deals, and coupon codes
                        </p>
                      </div>
                    </div>
                    <ArrowRightIcon className={`h-5 w-5 ${dk.textFaint}`} data-id="element-19975" data-mp="2cnqibq0x410t" />
                  </Link>
                  <Link to="/vendors/marketing/email-campaigns" className={`p-4 flex items-center justify-between hover:${dk.surfaceMuted} transition-colors`} data-id="element-19976" data-mp="2grg3e5idw0h3">
                    <div className="flex items-center" data-id="element-19977" data-mp="mdx0eg5l3poe">
                      <div className={`p-2 rounded-md mr-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-19978" data-mp="rax4iliviicv">
                        <MailIcon className={`h-5 w-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-19979" data-mp="ju0l9sqa40by" />
                      </div>
                      <div data-id="element-19980" data-mp="a2nrgvkx703a">
                        <h3 className={`font-medium ${dk.textPrimary}`} data-id="element-19981" data-mp="9zrtx8czidfd">
                          Email Campaigns
                        </h3>
                        <p className={`text-sm ${dk.textMuted}`} data-id="element-19982" data-mp="12pqp7lhrv6sh">
                          Newsletters and automated sequences
                        </p>
                      </div>
                    </div>
                    <ArrowRightIcon className={`h-5 w-5 ${dk.textFaint}`} data-id="element-19983" data-mp="srkkyx8htokg" />
                  </Link>
                  <Link to="/vendors/marketing/promo-graphics" className={`p-4 flex items-center justify-between hover:${dk.surfaceMuted} transition-colors`} data-id="element-19984" data-mp="oq8vcrpmd3c9">
                    <div className="flex items-center" data-id="element-19985" data-mp="39fy911el6j3d">
                      <div className={`p-2 rounded-md mr-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-19986" data-mp="qyttmijo63l5">
                        <ImageIcon className={`h-5 w-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-19987" data-mp="mvniawhp9m7t" />
                      </div>
                      <div data-id="element-19988" data-mp="vwvbx199tyvk">
                        <h3 className={`font-medium ${dk.textPrimary}`} data-id="element-19989" data-mp="vwlqlqz68fw7">
                          Promo Graphics
                        </h3>
                        <p className={`text-sm ${dk.textMuted}`} data-id="element-19990" data-mp="15n6jqyw2ss7q">
                          Create and manage visual assets
                        </p>
                      </div>
                    </div>
                    <ArrowRightIcon className={`h-5 w-5 ${dk.textFaint}`} data-id="element-19991" data-mp="1epzcz7otje07" />
                  </Link>
                  <Link to="/vendors/marketing/social-templates" className={`p-4 flex items-center justify-between hover:${dk.surfaceMuted} transition-colors`} data-id="element-19992" data-mp="3lgfcen9549pz">
                    <div className="flex items-center" data-id="element-19993" data-mp="1vz0iw0ozifwd">
                      <div className={`p-2 rounded-md mr-4 ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`} data-id="element-19994" data-mp="20xv2h4us08hj">
                        <LayoutIcon className={`h-5 w-5 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-19995" data-mp="1n3uactybxjq5" />
                      </div>
                      <div data-id="element-19996" data-mp="1d664p8z6qrt7">
                        <h3 className={`font-medium ${dk.textPrimary}`} data-id="element-19997" data-mp="1da3kbkmk118c">
                          Social Templates
                        </h3>
                        <p className={`text-sm ${dk.textMuted}`} data-id="element-19998" data-mp="14844gvna036z">
                          Ready-to-use social media kits
                        </p>
                      </div>
                    </div>
                    <ArrowRightIcon className={`h-5 w-5 ${dk.textFaint}`} data-id="element-19999" data-mp="2ym50l8rfyc3d" />
                  </Link>
                </div>
              </div>
            </section>
          </div>

          {/* Sidebar Area */}
          <div className="space-y-8" data-id="element-20000" data-mp="ff13tl1eh24k">
            {/* Recent Activity */}
            <section data-id="element-20001" data-mp="aevfadqffvqp">
              <h2 className={`text-xl font-bold mb-4 ${dk.textHeading}`} data-id="element-20002" data-mp="aed8in651z4j">
                Recent Activity
              </h2>
              <div className={`rounded-xl border p-4 ${dk.card}`} data-id="element-20003" data-mp="6fnquie2hvf3">
                <div className="space-y-6" data-id="element-20004" data-mp="1frvt8nclebo">
                  {recentActivity.map((item, index) => <div key={item.id} className="relative flex gap-4" data-id="element-20005" data-mp="y3fto4zzea3s" data-mpi={index}>
                      {/* Connector Line */}
                      {index !== recentActivity.length - 1 && <div className={`absolute left-[19px] top-10 bottom-[-24px] w-0.5 ${darkMode ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-20006" data-mp="pbdt86pwwtti" />}

                      <div className={`relative z-10 h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 ${darkMode ? 'bg-gray-800 border border-gray-700' : 'bg-white border border-gray-200'}`} data-id="element-20007" data-mp="1hti1uky12cny">
                        <item.icon className={`h-5 w-5 ${item.iconColor}`} data-id="element-20008" data-mp="1pbzirc88gtqy" />
                      </div>
                      <div className="pt-1" data-id="element-20009" data-mp="1uasvv3jlyiql">
                        <p className={`text-sm font-medium ${dk.textPrimary}`} data-id="element-20010" data-mp="1ua0xxm2jg8x5">
                          {item.action}
                        </p>
                        <p className={`text-sm ${dk.textSecondary}`} data-id="element-20011" data-mp="23caaehhdacd6">
                          {item.target}
                        </p>
                        <div className="flex items-center mt-1 gap-2" data-id="element-20012" data-mp="289sg7z038bgy">
                          <span className={`text-xs ${dk.textMuted}`} data-id="element-20013" data-mp="2d1jzdogwzex2">
                            {item.time}
                          </span>
                          <span className={`text-[10px] px-1.5 py-0.5 ${rt.badgeRadius} font-medium ${item.status === 'Sent' || item.status === 'Active' || item.status === 'Completed' ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}`} data-id="element-20014" data-mp="2i03kxnrlioow">
                            {item.status}
                          </span>
                        </div>
                      </div>
                    </div>)}
                </div>
                <button className={`w-full mt-6 py-2 text-sm font-medium border rounded-lg transition-colors ${dk.border} ${dk.textSecondary} hover:${dk.surfaceMuted}`} data-id="element-20015" data-mp="2i6vvzqh1k1qh">
                  View All Activity
                </button>
              </div>
            </section>

            {/* Tips Card */}
            <div className={`rounded-xl p-5 border ${darkMode ? 'bg-purple-900/10 border-purple-900/30' : 'bg-purple-50 border-purple-100'}`} data-id="element-20016" data-mp="24btjulllpg58">
              <div className="flex items-start gap-3" data-id="element-20017" data-mp="3frrxz3vmysdb">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/50 rounded-lg text-purple-600 dark:text-purple-400" data-id="element-20018" data-mp="3aurqcoj51hk3">
                  <MegaphoneIcon className="h-5 w-5" data-id="element-20019" data-mp="wlfpe5wv4dur" />
                </div>
                <div data-id="element-20020" data-mp="11m3hyw0qp2r3">
                  <h3 className={`font-bold text-sm ${darkMode ? 'text-purple-200' : 'text-purple-900'}`} data-id="element-20021" data-mp="11mn2mkhry58t">
                    Pro Tip
                  </h3>
                  <p className={`text-sm mt-1 ${darkMode ? 'text-purple-300' : 'text-purple-700'}`} data-id="element-20022" data-mp="slx61ekowau1">
                    Running a discount? Don't forget to create matching social
                    graphics to announce it to your followers!
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default MarketingTools;